import { useEffect, useState, useSyncExternalStore } from 'react';

import { isIosNative } from '@/renderer/utils/isIosNative';
import { getKeyboardState, subscribeKeyboard } from '@/renderer/utils/keyboard';

/**
 * Whether the on-screen keyboard is currently up.
 *
 * Two platforms, two signals:
 *
 *  - Native iOS reads the app-wide keyboard store (renderer/utils/keyboard.ts),
 *    which follows the Keyboard plugin's `keyboardWillShow` / `keyboardWillHide`.
 *    Those fire BEFORE the animation, the earliest and steadiest moment to
 *    react, and they are the same events that drive `--kb-inset`, so the tab
 *    bar can never disagree with the layout about whether the keyboard is up.
 *  - Android's WebView and every browser leave the layout viewport alone and
 *    shrink only the VISUAL viewport, so the gap between the two is the
 *    keyboard. The threshold keeps a collapsing URL bar or a browser toolbar
 *    (tens of px) from reading as a keyboard.
 *
 * SSR-safe: returns false until hydration, which is also the right answer for
 * a freshly rendered page.
 */

/** Below this, a visual-viewport shrink is browser chrome, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 120;

const noopSubscribe = () => () => undefined;

export function useKeyboardOpen(): boolean {
  const iosNative = typeof window !== 'undefined' && isIosNative();
  const iosOpen = useSyncExternalStore(
    iosNative ? subscribeKeyboard : noopSubscribe,
    () => iosNative && getKeyboardState().open,
    () => false,
  );
  const [viewportOpen, setViewportOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || isIosNative()) {
      return;
    }

    const viewport = window.visualViewport;

    if (!viewport) {
      return;
    }

    const read = () => {
      setViewportOpen(window.innerHeight - viewport.height > KEYBOARD_MIN_HEIGHT);
    };

    read();
    viewport.addEventListener('resize', read);

    return () => {
      viewport.removeEventListener('resize', read);
    };
  }, []);

  return iosNative ? iosOpen : viewportOpen;
}
